// Collecteurs sur site : libellés, saisie de l'empreinte courte et messages d'erreur par code fermé.
// Règle O63 : l'API ne rend jamais la clé, le kid ni l'empreinte d'une demande en attente ; l'administrateur lit
// l'empreinte courte SUR LE SERVEUR DU COLLECTEUR et la saisit. Rien de tout cela n'est affiché, gardé ni journalisé ici.
import { ErreurApi, expliquerErreur } from '../api/client';
import type { CollecteurVue, EtatCollecteur } from '../api/types';
import { dateFr } from './format';

export const LIBELLE_ETAT_COLLECTEUR: Record<EtatCollecteur, string> = {
  en_attente: 'En attente',
  expiree: 'Demande expirée',
  actif: 'Actif',
  desactive: 'Désactivé',
  refuse: 'Refusé',
  revoque: 'Révoqué',
};

export const TON_ETAT_COLLECTEUR: Record<EtatCollecteur, 'ok' | 'attention' | 'grave' | 'neutre'> = {
  en_attente: 'attention',
  expiree: 'neutre',
  actif: 'ok',
  desactive: 'neutre',
  refuse: 'grave',
  revoque: 'grave',
};

export const EXPLICATION_ETAT_COLLECTEUR: Record<EtatCollecteur, string> = {
  en_attente: 'La demande d’enrôlement attend l’approbation de deux administrateurs.',
  expiree: 'La demande n’a pas été approuvée à temps : le collecteur doit la renouveler.',
  actif: 'Le collecteur dépose ses lectures signées.',
  desactive: 'Arrêt réversible : les sources restent liées mais ne sont pas servies.',
  refuse: 'La demande a été refusée (définitif).',
  revoque: 'Révocation définitive : clés révoquées, sources déliées.',
};

/** L'état est celui d'un collecteur qui n'a jamais eu de clé active : aucune empreinte, aucun identifiant de clé. */
export const SANS_CLE_VISIBLE: readonly EtatCollecteur[] = ['en_attente', 'expiree', 'refuse'];

export const NOMBRE_HEXA_EMPREINTE = 16;

/**
 * Normalise la saisie de l'empreinte courte : espaces, tirets et séparateurs usuels tolérés, casse indifférente.
 * Rend les 16 hexadécimaux en minuscules, ou `null` si la saisie n'a pas le bon format.
 */
export function normaliserEmpreinte(saisie: string): string | null {
  const net = saisie.replace(/[\s  \-‐-―:.]/g, '').toLowerCase();
  return /^[0-9a-f]{16}$/.test(net) ? net : null;
}

/** Nombre de caractères utiles déjà saisis (aide à la saisie : on ne montre jamais la valeur elle-même). */
export function caracteresSaisis(saisie: string): number {
  return saisie.replace(/[\s  \-‐-―:.]/g, '').length;
}

/** Empreinte courte déjà connue (clé active) par groupes de quatre (l'API la rend déjà groupée : on regroupe sans doubler). */
export function groupesDeQuatre(empreinte: string): string {
  return empreinte.replace(/\s+/g, '').replace(/(.{4})(?=.)/g, '$1 ');
}

export const LIBELLE_ACTION_JOURNAL_COLLECTEUR: Record<string, string> = {
  demande: 'Demande d’enrôlement',
  approbation: 'Approbation',
  refus_demande: 'Demande refusée',
  activation: 'Activation',
  desactivation: 'Désactivation',
  reactivation: 'Réactivation',
  revocation: 'Révocation',
  rotation: 'Renouvellement de la clé',
  refus: 'Refus de signature',
};

export const LIBELLE_RAISON_REVOCATION = {
  compromission: 'Compromission (clé ou serveur compromis)',
  perte: 'Perte de la clé ou du serveur',
  fin_de_service: 'Fin de service du collecteur',
  autre: 'Autre raison',
} as const;

const LIBELLE_DETAIL: Record<string, string> = {
  nom: 'Nom',
  nom_propose: 'Nom proposé',
  motif: 'Motif',
  raison: 'Raison',
  compromis_depuis: 'Compromis depuis',
  sources_deliees: 'Sources déliées',
  rang: 'Approbation n°',
  approbations: 'Approbations',
  quatre_yeux: 'Quatre yeux',
  code: 'Code',
  route: 'Route',
};

/**
 * Détail d'une entrée du journal, lisible. Liste blanche : toute autre rubrique (clé, identifiant de clé, empreinte…) est
 * ignorée, même si l'API en rendait une (défense en profondeur, O63).
 */
export function detailLisible(detail: Record<string, unknown>): { cle: string; libelle: string; valeur: string }[] {
  const sortie: { cle: string; libelle: string; valeur: string }[] = [];
  for (const [cle, libelle] of Object.entries(LIBELLE_DETAIL)) {
    const v = detail[cle];
    if (v === undefined || v === null || v === '') continue;
    let valeur: string;
    if (typeof v === 'boolean') valeur = v ? 'oui' : 'non';
    else if (typeof v === 'string' || typeof v === 'number') valeur = String(v);
    else continue;
    if (cle === 'raison' && valeur in LIBELLE_RAISON_REVOCATION) valeur = LIBELLE_RAISON_REVOCATION[valeur as keyof typeof LIBELLE_RAISON_REVOCATION];
    sortie.push({ cle, libelle, valeur });
  }
  return sortie;
}

/**
 * Message français d'un refus de l'API sur les collecteurs, d'après le code fermé. Ne recopie jamais le message de l'API ni
 * une valeur saisie.
 */
export function expliquerErreurCollecteur(e: unknown): string {
  if (!(e instanceof ErreurApi)) return expliquerErreur(e);
  switch (e.code) {
    case 'compte_de_service':
      return 'Un compte de service ne peut pas approuver un enrôlement : l’approbation est réservée à une personne connectée par l’application d’Ekera.';
    case 'authentification_forte_requise':
      return 'Cette approbation exige une authentification forte. Reconnectez-vous avec le second facteur (MFA) puis réessayez.';
    case 'applications_humaines_non_declarees':
      return 'L’exploitant d’Ekera doit déclarer les applications humaines de votre organisation avant toute approbation. Contactez-le : l’enrôlement reste en attente.';
    case 'empreinte_differente':
      return 'L’empreinte saisie n’est pas celle de la demande. Relisez-la sur le serveur du collecteur et saisissez-la de nouveau. Ce refus est inscrit au journal de sécurité.';
    case 'approbation_deja_donnee':
      return 'Vous avez déjà approuvé cette demande. Le quatre-yeux exige une autre personne : un second administrateur, distinct de vous, doit la confirmer.';
    case 'enrolement_expire':
      return 'La demande d’enrôlement a expiré : le collecteur doit la renouveler depuis son serveur.';
    case 'deja_actif':
      return 'Ce collecteur est déjà actif.';
    case 'etat_incompatible':
      return 'Cette action n’est plus possible dans l’état actuel du collecteur : actualisez la page.';
    case 'collecteur_inconnu':
      return 'Collecteur inconnu, ou pas encore actif : une source ne se lie qu’à un collecteur actif.';
    case 'compromis_depuis_incoherent':
      return 'La date de compromission ne va qu’avec la raison « compromission ».';
    case 'date_future':
      return 'La date de compromission ne peut pas être dans le futur.';
    case 'cle_reutilisee':
      return 'Cette clé d’idempotence a déjà servi pour une autre écriture : recommencez l’action.';
    case 'cle_idempotence':
      return 'La demande est incomplète (clé d’idempotence absente) : rechargez la page.';
    case 'ecriture_concurrente':
      return 'Une autre écriture a eu lieu en même temps : actualisez, puis recommencez.';
    case 'requete_invalide':
      return 'Requête refusée : vérifiez les champs (aucune valeur n’est recopiée).';
    case 'curseur_invalide':
      return 'Le curseur du journal n’est plus valable : rechargez la fiche.';
    default:
      break;
  }
  if (e.statut === 403) return 'Réservé à l’administration : votre rôle ne permet pas cette action.';
  if (e.statut === 422) return 'Valeurs refusées : vérifiez les champs (aucune valeur n’est recopiée).';
  if (e.statut === 409) return 'Cette action est refusée dans l’état actuel du collecteur : actualisez la page.';
  return expliquerErreur(e);
}

/** Nom affiché : celui confirmé à l'approbation, sinon la date de la demande (le nom proposé n'est pas fiable, O77). */
export function nomCollecteur(c: Pick<CollecteurVue, 'nom' | 'cree_le'>): string {
  return c.nom ?? `Demande du ${dateFr(c.cree_le)}`;
}

/** Nom lisible d'une approbation ou d'une entrée de journal (déjà nettoyé par l'API, texte simple) ; à défaut le sujet. */
export function identiteLisible(x: { par: string; par_nom?: string | null }): string {
  return x.par_nom ? x.par_nom : x.par;
}

/** Nom lisible suivi de l'identifiant court quand l'API le donne (O80) : « <nom> · id a1b2c3d4 », texte simple. */
export function identiteAvecId(x: { par: string; par_nom?: string | null; par_id_court?: string | null }): string {
  return x.par_id_court ? `${identiteLisible(x)} · id ${x.par_id_court}` : identiteLisible(x);
}

/** Second repère, discret : l'identifiant (sujet) quand un nom lisible est affiché. */
export function sujetSecond(x: { par: string; par_nom?: string | null }): string | null {
  return x.par_nom ? x.par : null;
}

/**
 * Confort d'affichage (l'API reste seule juge) : cette approbation est-elle celle de la personne connectée ? Le couple
 * (émetteur, sujet) est comparé ; si l'approbation ne porte pas d'émetteur (entrée ancienne), le sujet seul décide.
 */
export function estMonApprobation(a: { par: string; emetteur?: string | null }, moi: { sujet: string; emetteur?: string | null }): boolean {
  if (a.par !== moi.sujet) return false;
  return !a.emetteur || !moi.emetteur || a.emetteur === moi.emetteur;
}
